/**
 * Slice 33 — Rooms is the first-visit onboarding checklist.
 * Capture → Import → extras for this room → next room.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronRight, GripVertical, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  usePropertyRooms,
  useCreatePropertyRoom,
  useArchivePropertyRoom,
  useUpdatePropertyRoom,
} from "@/hooks/usePropertyRooms";
import { useInventoryItems } from "@/hooks/useInventory";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useUnitItems } from "@/hooks/useUnitItems";
import { useAppliances } from "@/hooks/useAppliances";
import { normalizeRoomName } from "@/lib/roomUtils";
import { cn } from "@/lib/utils";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { usePropertyGalleryPhotos } from "@/hooks/usePropertyGalleryPhotos";
import { useReferencePhotos } from "@/hooks/useReferencePhotos";
import {
  readHospitableChecked,
  useRefreshHospitablePhotos,
  useRoomCoverData,
  useSaveRoomCovers,
  useSaveWalkOrder,
  useSetRoomNoPhoto,
} from "@/hooks/useRoomCoverPhotos";
import { coverProgressText, sortRoomsWalkOrder, suggestCovers } from "@/lib/roomCoverPhotos";
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { RoomCoverPicker } from "@/components/owner/RoomCoverPicker";
import { WalkthroughFlow, CapturePhoneHint } from "@/components/owner/WalkthroughFlow";
import { GuestGuideFlow } from "@/components/owner/GuestGuideFlow";
import { isDesktopCaptureViewport } from "@/lib/captureStill";
import {
  buildRoomSpineRows,
  extrasLeftByPropertyRoomId,
  extrasLeftForRoomName,
  nextUncapturedRoom,
  onboardIsComplete,
  primaryButtonLabel,
  primarySpineAction,
  roomsDoneCount,
  type RoomSpineRow,
} from "@/lib/onboardingSpine";

type Handoff =
  | { kind: "imported"; room: string; extras: number; nextName: string | null }
  | { kind: "extras_done"; room: string; nextName: string | null };

interface RoomsTabProps {
  propertyId: string;
}

export default function RoomsTab({ propertyId }: RoomsTabProps) {
  const { data: roomsRaw = [], isLoading } = usePropertyRooms(propertyId);
  const createRoom = useCreatePropertyRoom();
  const archiveRoom = useArchivePropertyRoom();
  const updateRoom = useUpdatePropertyRoom();
  const { data: unitItems = [] } = useUnitItems(propertyId);
  const { data: invItems = [] } = useInventoryItems(propertyId);
  const { data: invRooms = [] } = useInventoryRooms(propertyId);
  const { data: appliances = [] } = useAppliances(propertyId);

  const { data: coverData = {} } = useRoomCoverData(propertyId);
  const saveCovers = useSaveRoomCovers(propertyId);
  const saveOrder = useSaveWalkOrder(propertyId);
  const setNoPhoto = useSetRoomNoPhoto(propertyId);
  const [uploadingCover, setUploadingCover] = useState(false);
  const { data: propertyForPhotos } = usePropertyWithAccess(propertyId);
  const gallery = usePropertyGalleryPhotos(
    propertyId,
    propertyForPhotos as { property_photo_url?: string | null; property_photo_urls?: string[] | null } | null | undefined,
  );
  const { data: referencePhotos } = useReferencePhotos(propertyId, null);
  const { refresh: refreshPhotos, isRefreshing } = useRefreshHospitablePhotos(propertyId);

  // Walk order: the saved order, else the order rooms were added (what this tab always showed).
  const rooms = useMemo(() => sortRoomsWalkOrder(roomsRaw, coverData), [roomsRaw, coverData]);

  const [pickRoomId, setPickRoomId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<{ ids: string[]; text: string } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = (ids: string[], text: string) => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setSavedFlash({ ids, text });
    flashTimer.current = setTimeout(() => setSavedFlash(null), 3000);
  };
  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const savedCovers = useMemo(() => {
    const m: Record<string, string | null> = {};
    for (const r of rooms) m[r.id] = coverData[r.id]?.cover_photo_url ?? null;
    return m;
  }, [rooms, coverData]);
  const noPhotoIds = useMemo(
    () => new Set(rooms.filter((r) => coverData[r.id]?.cover_not_needed).map((r) => r.id)),
    [rooms, coverData],
  );
  const suggestions = useMemo(() => {
    // A room marked "no photo needed" is settled: it never gets a suggestion.
    const settled: Record<string, string | null> = { ...savedCovers };
    for (const id of noPhotoIds) settled[id] = "none";
    return suggestCovers(rooms, settled, referencePhotos?.list ?? []);
  }, [rooms, savedCovers, noPhotoIds, referencePhotos]);
  const coverCount = rooms.filter((r) => savedCovers[r.id]).length;
  const noPhotoCount = noPhotoIds.size;
  const suggestedIds = rooms.filter((r) => !savedCovers[r.id] && !noPhotoIds.has(r.id) && suggestions[r.id]).map((r) => r.id);
  /** Photo URL → name of another room that uses it (saved cover or suggestion). */
  const usedBy = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of rooms) {
      if (r.id === pickRoomId) continue;
      const url = savedCovers[r.id] ?? suggestions[r.id];
      if (url && !m[url]) m[url] = r.name;
    }
    return m;
  }, [rooms, pickRoomId, savedCovers, suggestions]);

  const [walkRoom, setWalkRoom] = useState<string | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [desktopCapture, setDesktopCapture] = useState(false);
  const [guideRoom, setGuideRoom] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const [importedRoom, setImportedRoom] = useState<string | null>(null);
  const importedInventoryHint = useRef(0);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<{ id: string; name: string } | null>(null);
  const [pendingRename, setPendingRename] = useState<{ id: string; name: string } | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const addInputRef = useRef<HTMLInputElement>(null);
  const typedRoomRef = useRef("");
  const savingRoomRef = useRef(false);

  const roomNames = rooms.map((r) => r.name);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const room of rooms) {
      const n = normalizeRoomName(room.name);
      const invRoomIds = new Set(
        invRooms
          .filter(
            (ir) => ir.property_room_id === room.id || normalizeRoomName(ir.name) === n,
          )
          .map((ir) => ir.id),
      );
      const invCount = (invItems as { room_id?: string | null }[]).filter((it) =>
        it.room_id ? invRoomIds.has(it.room_id) : false,
      ).length;
      const unitCount = unitItems.filter((it) => normalizeRoomName(it.room || "") === n).length;
      const apCount = appliances.filter((it) => normalizeRoomName(it.location || "") === n).length;
      map[room.id] = invCount + unitCount + apCount;
    }
    return map;
  }, [appliances, invItems, invRooms, rooms, unitItems]);

  const extrasLeftByRoomId = useMemo(
    () =>
      extrasLeftByPropertyRoomId(
        rooms,
        invRooms,
        invItems as { room_id?: string | null; guest_guide_status?: string | null }[],
      ),
    [invItems, invRooms, rooms],
  );

  const spinePreview = useMemo(
    () =>
      buildRoomSpineRows({
        rooms,
        itemCountByRoomId: counts,
        extrasLeftByRoomId,
        onboardComplete: false,
      }),
    [counts, extrasLeftByRoomId, rooms],
  );
  const onboardComplete = onboardIsComplete(spinePreview);
  const rows = useMemo(
    () =>
      buildRoomSpineRows({
        rooms,
        itemCountByRoomId: counts,
        extrasLeftByRoomId,
        onboardComplete,
      }),
    [counts, extrasLeftByRoomId, onboardComplete, rooms],
  );
  const doneCount = roomsDoneCount(rows);
  const primary = primarySpineAction(rows);
  const secondaryCapture =
    primary?.kind === "extras" ? nextUncapturedRoom(rows, primary.room.name) : null;

  const saveRoom = async () => {
    const name = (typedRoomRef.current || addInputRef.current?.value || "").trim();
    if (!name) {
      toast({ title: "Type a room name", variant: "destructive" });
      return;
    }
    if (savingRoomRef.current) return;
    savingRoomRef.current = true;
    setBusy(true);
    try {
      await createRoom.mutateAsync({ propertyId, name });
      typedRoomRef.current = "";
      if (addInputRef.current) addInputRef.current.value = "";
      setAddOpen(false);
      toast({ title: name });
    } catch (e) {
      const msg =
        e && typeof e === "object" && "message" in e ? String((e as { message: string }).message) : "Try again";
      toast({
        title: "Couldn’t add room",
        description: msg,
        variant: "destructive",
      });
    } finally {
      savingRoomRef.current = false;
      setBusy(false);
    }
  };

  const renameRoom = async () => {
    if (!pendingRename) return;
    const next = renameValue.trim();
    if (!next) {
      toast({ title: "Type a room name", variant: "destructive" });
      return;
    }
    if (next === pendingRename.name) {
      setPendingRename(null);
      return;
    }
    setBusy(true);
    try {
      await updateRoom.mutateAsync({ propertyId, roomId: pendingRename.id, name: next });
      toast({ title: `Renamed to ${next}` });
      setPendingRename(null);
    } catch (e) {
      toast({
        title: "Couldn’t rename room",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const removeRoom = async (roomId: string, name: string) => {
    setBusy(true);
    try {
      await archiveRoom.mutateAsync({ propertyId, roomId });
      toast({ title: `${name} removed` });
      setPendingRemove(null);
    } catch (e) {
      toast({
        title: "Couldn’t remove room",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const walkOpen = Boolean(pickOpen || walkRoom);

  const startCapture = (room?: string | null) => {
    setHandoff(null);
    setGuideRoom(null);
    if (isDesktopCaptureViewport()) {
      setDesktopCapture(true);
      return;
    }
    if (room) setWalkRoom(room);
    else setPickOpen(true);
  };

  const startExtras = (room: string) => {
    setHandoff(null);
    setGuideRoom(room);
  };

  // The walk now sets extras and the guest note on each item and ends on its own "saved" screen,
  // so there is nothing to open afterwards. Item queries refresh from the saves themselves.
  const afterImport = (_room: string, _inventoryImported = 0) => undefined;

  useEffect(() => {
    if (!importedRoom) return;
    const computed = extrasLeftForRoomName(extrasLeftByRoomId, rooms, importedRoom);
    const extras = computed > 0 ? computed : importedInventoryHint.current;
    const roomId = rooms.find((r) => normalizeRoomName(r.name) === normalizeRoomName(importedRoom))?.id;
    const itemCount = roomId ? counts[roomId] ?? 0 : 0;
    if (extras <= 0 && itemCount <= 0 && importedInventoryHint.current <= 0) return;
    const next = nextUncapturedRoom(rows, importedRoom);
    if (extras > 0) {
      setHandoff({ kind: "imported", room: importedRoom, extras, nextName: next?.name ?? null });
    } else {
      setHandoff({ kind: "extras_done", room: importedRoom, nextName: next?.name ?? null });
    }
    importedInventoryHint.current = 0;
    setImportedRoom(null);
  }, [counts, extrasLeftByRoomId, importedRoom, rooms, rows]);

  const afterExtrasClose = () => {
    setGuideRoom(null);
  };

  const onRow = (row: RoomSpineRow) => {
    // Re-open guest place flow for finished rooms so qty/place edits stay in Hospitable KB.
    if (row.state === "extras_left" || row.state === "extras_done") startExtras(row.name);
    else startCapture(row.name);
  };

  if (isLoading && !addOpen) {
    return (
      <div className="flex justify-center py-16 text-[#64748b]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const walkOrderLine = "In walk order. Cleaners go room by room in this order.";
  const subtitle = !rooms.length
    ? "Add the rooms, then start in the first one."
    : doneCount === 0 && !onboardComplete
      ? "Start in the first one. You’ll finish each room before moving on."
      : walkOrderLine;

  const pickRoom = rooms.find((r) => r.id === pickRoomId) ?? null;

  const choosePhoto = async (url: string) => {
    if (!pickRoom) return;
    const { id, name } = pickRoom;
    setPickRoomId(null);
    try {
      await saveCovers.mutateAsync({ [id]: url });
      flash([id], `Cover photo saved · ${name}`);
    } catch (e) {
      toast({
        title: "Couldn’t save cover photo",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    }
  };

  const chooseNoPhoto = async () => {
    if (!pickRoom) return;
    const { id, name } = pickRoom;
    setPickRoomId(null);
    try {
      await setNoPhoto.mutateAsync(id);
      flash([id], `No photo needed · ${name}`);
    } catch (e) {
      toast({
        title: "Couldn’t save",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    }
  };

  const uploadCover = async (file: File) => {
    if (!pickRoom) return;
    const { id, name } = pickRoom;
    setUploadingCover(true);
    try {
      const prepared = await prepareImageFileForUpload(file);
      const path = `${propertyId}/room_${id}_${Date.now()}.jpg`;
      const { error } = await supabase.storage.from("property-photos").upload(path, prepared, {
        upsert: true,
        contentType: "image/jpeg",
      });
      if (error) throw error;
      const { data } = supabase.storage.from("property-photos").getPublicUrl(path);
      if (!data?.publicUrl) throw new Error("Couldn’t get the photo link");
      setPickRoomId(null);
      await saveCovers.mutateAsync({ [id]: data.publicUrl });
      flash([id], `Cover photo saved · ${name}`);
    } catch (e) {
      toast({
        title: "Couldn’t upload the photo",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setUploadingCover(false);
    }
  };

  const confirmSuggestions = async () => {
    const covers: Record<string, string> = {};
    for (const id of suggestedIds) covers[id] = suggestions[id];
    try {
      await saveCovers.mutateAsync(covers);
      flash(suggestedIds, `${suggestedIds.length} cover photos saved`);
    } catch (e) {
      toast({
        title: "Couldn’t save cover photos",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    }
  };

  const moveRoom = (fromId: string | null, toId: string) => {
    if (!fromId || fromId === toId) return;
    const ids = rooms.map((r) => r.id);
    const from = ids.indexOf(fromId);
    if (from < 0) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(Math.max(0, ids.indexOf(toId)), 0, moved);
    saveOrder.mutate(ids, {
      onSuccess: () => flash([], "Walk order saved"),
      onError: (e) =>
        toast({
          title: "Couldn’t save walk order",
          description: e instanceof Error ? e.message : undefined,
          variant: "destructive",
        }),
    });
  };

  const refreshFromHospitable = async () => {
    try {
      await refreshPhotos();
    } catch (e) {
      toast({
        title: "Couldn’t refresh photos",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col gap-5 px-px pb-8">
      <WalkthroughFlow
        open={walkOpen}
        propertyId={propertyId}
        roomName={pickOpen ? null : walkRoom}
        roomNames={roomNames}
        onClose={() => {
          setWalkRoom(null);
          setPickOpen(false);
        }}
        onImported={afterImport}
      />
      {guideRoom ? (
        <GuestGuideFlow
          open
          propertyId={propertyId}
          roomName={guideRoom}
          nextRoomName={nextUncapturedRoom(rows, guideRoom)?.name ?? null}
          onClose={() => afterExtrasClose()}
          onAddItem={() => {
            const room = guideRoom;
            setGuideRoom(null);
            if (room) startCapture(room);
          }}
          onGoNext={() => {
            const next = nextUncapturedRoom(rows, guideRoom)?.name ?? null;
            setGuideRoom(null);
            if (next) startCapture(next);
          }}
        />
      ) : null}
      {desktopCapture ? (
        createPortal(
          <div className="fixed inset-0 z-[80] bg-[#f4f7f9]" data-ptr-ignore>
            <CapturePhoneHint propertyId={propertyId} onClose={() => setDesktopCapture(false)} />
          </div>,
          document.body,
        )
      ) : null}
      {pickRoom
        ? createPortal(
            <RoomCoverPicker
              roomName={pickRoom.name}
              propertyName={(propertyForPhotos as { name?: string } | null | undefined)?.name || "this property"}
              photos={gallery}
              usedBy={usedBy}
              selectedUrl={savedCovers[pickRoom.id] ?? suggestions[pickRoom.id] ?? null}
              loading={isRefreshing}
              lastChecked={readHospitableChecked(propertyId)}
              noPhotoSelected={noPhotoIds.has(pickRoom.id)}
              uploading={uploadingCover}
              onNoPhoto={() => void chooseNoPhoto()}
              onUpload={(f) => void uploadCover(f)}
              onPick={(url) => void choosePhoto(url)}
              onRefresh={() => void refreshFromHospitable()}
              onClose={() => setPickRoomId(null)}
            />,
            document.body,
          )
        : null}
      {savedFlash
        ? createPortal(
            <div className="fixed bottom-6 left-1/2 z-[80] flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-[#0f1720] px-5 py-3 text-[14.5px] font-bold text-white shadow-[0_12px_30px_rgba(15,23,32,0.3)]">
              <Check className="h-4 w-4 text-[#5fd3c5]" strokeWidth={3} />
              {savedFlash.text}
            </div>,
            document.body,
          )
        : null}
      {handoff ? (
        <HandoffScreen
          handoff={handoff}
          rows={rows}
          doneCount={doneCount}
          onStartExtras={() => startExtras(handoff.room)}
          onSkipToNext={() => {
            if (handoff.nextName) startCapture(handoff.nextName);
            else setHandoff(null);
          }}
          onGoNext={() => {
            if (handoff.nextName) startCapture(handoff.nextName);
            else setHandoff(null);
          }}
          onClose={() => setHandoff(null)}
        />
      ) : null}

      {rooms.length > 0 && !onboardComplete && doneCount > 0 ? (
        <div className="px-1">
          <div className="text-[11px] font-bold tracking-[0.8px] text-[#15776d]">ONBOARDING</div>
          <div className="mt-1 text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">
            {doneCount} of {rows.length} rooms done
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="sr-only">Rooms</h2>
            {subtitle ? <p className="mt-1 text-[15px] font-medium text-[#64748b]">{subtitle}</p> : null}
          </div>
        </div>
      )}

      {addOpen ? (
        <AddRoomSheet
          busy={busy}
          inputRef={addInputRef}
          onClose={() => {
            typedRoomRef.current = "";
            setAddOpen(false);
          }}
          onTyped={(value) => {
            typedRoomRef.current = value;
          }}
          onSave={() => void saveRoom()}
        />
      ) : null}

      {rooms.length === 0 && !addOpen ? (
        <div className="flex flex-col items-center gap-2 rounded-[18px] border-[1.5px] border-dashed border-[#d7e0e6] bg-white px-5 py-10">
          <div className="text-[16px] font-bold text-[#64748b]">No rooms yet</div>
          <div className="text-[13.5px] font-medium text-[#94a3b8]">
            Name them the way you’d say them out loud — Kitchen, Bath, Bedroom 1.
          </div>
        </div>
      ) : rooms.length > 0 ? (
        <>
        <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
          <div className="flex flex-wrap items-center gap-x-[18px] gap-y-2 border-b border-[#e6edf1] px-5 py-4">
            <div className="min-w-0 flex-1 text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">
              {coverProgressText(coverCount, rooms.length, noPhotoCount)}
            </div>
            <div className="flex w-full gap-1 sm:w-[220px]">
              {rooms.map((r, i) => (
                <div key={r.id} className={cn("h-2 flex-1 rounded-full", i < coverCount + noPhotoCount ? "bg-[#24a89a]" : "bg-[#e6edf1]")} />
              ))}
            </div>
          </div>
          {suggestedIds.length > 0 ? (
            <div className="flex flex-wrap items-center gap-4 border-b border-[#e6edf1] bg-[#f2f9f8] px-5 py-3.5">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="text-[15px] font-bold text-[#0f1720]">
                  We suggested {suggestedIds.length} cover photos. Check these.
                </div>
                <div className="text-[13px] font-medium text-[#64748b] max-sm:hidden">
                  Marked Suggested below. Change any you don’t like, then confirm.
                </div>
              </div>
              <button
                type="button"
                onClick={() => void confirmSuggestions()}
                disabled={saveCovers.isPending}
                className="flex h-11 items-center rounded-xl bg-[#24a89a] px-5 text-[14.5px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
              >
                Confirm {suggestedIds.length} photos
              </button>
            </div>
          ) : null}
          {rows.map((row, i) => {
            const saved = savedCovers[row.id] ?? null;
            const sug = !saved ? suggestions[row.id] ?? null : null;
            const shown = saved ?? sug;
            const justSaved = !!savedFlash?.ids.includes(row.id);
            const dropLine = !!dragId && overId === row.id && dragId !== row.id;
            const chooseBtn = (
              <button
                type="button"
                onClick={() => setPickRoomId(row.id)}
                className={cn(
                  "flex h-11 shrink-0 items-center rounded-xl px-4 text-[14px] font-bold",
                  saved || noPhotoIds.has(row.id)
                    ? "border-[1.5px] border-[#d7e0e6] bg-white text-[#475569] hover:border-[#24a89a] hover:text-[#15776d]"
                    : "bg-[#24a89a] text-white hover:bg-[#1b8378]",
                )}
              >
                Choose cover photo
              </button>
            );
            return (
              <div
                key={row.id}
                onDragOver={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  if (overId !== row.id) setOverId(row.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragId;
                  setDragId(null);
                  setOverId(null);
                  moveRoom(from, row.id);
                }}
                className={cn(
                  "flex w-full items-center gap-2 bg-white px-3 py-3 sm:gap-3.5 sm:pr-5",
                  i < rows.length - 1 && "border-b border-[#eef2f5]",
                  dropLine && "shadow-[inset_0_3px_0_#24a89a]",
                  justSaved && "shadow-[inset_0_0_0_2px_#24a89a]",
                  dragId === row.id && "opacity-45",
                )}
              >
                <div
                  draggable
                  aria-label="Drag to reorder"
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    try {
                      e.dataTransfer.setData("text/plain", row.id);
                    } catch {
                      /* ignore */
                    }
                    const rowEl = (e.currentTarget as HTMLElement).parentElement;
                    if (rowEl) e.dataTransfer.setDragImage(rowEl, 20, 20);
                    setDragId(row.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  className="flex h-11 w-7 shrink-0 cursor-grab items-center justify-center text-[#94a3b0]"
                >
                  <GripVertical className="h-5 w-5" />
                </div>
                <div className="w-[22px] shrink-0 text-center text-[14px] font-bold text-[#8494a2] max-sm:hidden">{i + 1}</div>
                {!shown && noPhotoIds.has(row.id) ? (
                  <div className="flex h-[76px] w-[112px] shrink-0 items-center justify-center rounded-[10px] border border-[#e6edf1] bg-[#eef2f5] text-center text-[12px] font-bold leading-[1.3] text-[#64748b] max-sm:h-[64px] max-sm:w-[92px]">
                    No photo<br />needed
                  </div>
                ) : shown ? (
                  <img
                    src={shown}
                    alt=""
                    loading="lazy"
                    className="h-[76px] w-[112px] shrink-0 rounded-[10px] border border-[#e6edf1] object-cover max-sm:h-[64px] max-sm:w-[92px]"
                  />
                ) : (
                  <div className="flex h-[76px] w-[112px] shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-dashed border-[#b9c6cf] bg-[#fafcfd] text-center text-[12px] font-bold leading-[1.3] text-[#8494a2] max-sm:h-[64px] max-sm:w-[92px]">
                    No cover<br />photo
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <button type="button" onClick={() => onRow(row)} className="min-w-0 text-left">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-bold text-[#8494a2] sm:hidden">{i + 1}</span>
                      <span className="truncate text-[16px] font-bold text-[#0f1720]">{row.name}</span>
                      {sug ? (
                        <span className="rounded-full border border-[#cfe5e2] px-2 py-[3px] text-[11.5px] font-bold text-[#15776d]">
                          Suggested
                        </span>
                      ) : null}
                      {justSaved && saved ? (
                        <span className="flex items-center gap-1 text-[13px] font-bold text-[#15776d] sm:hidden">
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                          Saved
                        </span>
                      ) : null}
                    </div>
                    {justSaved && saved ? (
                      <div className="flex items-center gap-[5px] text-[13px] font-bold text-[#15776d] max-sm:hidden">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        Saved
                      </div>
                    ) : (
                      <div className="text-[13px] font-medium text-[#8494a2] max-sm:hidden">
                        {saved
                          ? "Cleaners see this photo first"
                          : noPhotoIds.has(row.id)
                            ? "Cleaners see nothing here"
                            : sug
                              ? "Suggested from Hospitable"
                              : "Cleaners see the room name only"}
                      </div>
                    )}
                    <div
                      className={`text-[12.5px] font-medium ${
                        row.state === "extras_left" ? "text-[#15776d]" : "text-[#94a3b8]"
                      }`}
                    >
                      {row.meta}
                    </div>
                  </button>
                  <div className="sm:hidden">{chooseBtn}</div>
                </div>
                <div className="max-sm:hidden">{chooseBtn}</div>
                <ChevronRight className="h-[18px] w-[18px] shrink-0 text-[#c3ccd2] max-sm:hidden" />
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Rename ${row.name}`}
                  onClick={() => {
                    setRenameValue(row.name);
                    setPendingRename({ id: row.id, name: row.name });
                  }}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] text-[#64748b] max-sm:h-9 max-sm:w-9"
                >
                  <Pencil className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Remove ${row.name}`}
                  onClick={() => setPendingRemove({ id: row.id, name: row.name })}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] text-[#64748b] max-sm:h-9 max-sm:w-9"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            );
          })}
        </div>
        <div className="px-1 text-[12.5px] font-medium text-[#8494a2]">
          <span className="max-sm:hidden">Drag a room by its handle to change the walk order.</span>
          <span className="sm:hidden">Hold and drag a room to change the walk order.</span>
        </div>
        </>
      ) : null}

      {rooms.length > 0 && !addOpen ? (
        <button
          type="button"
          onClick={() => {
            typedRoomRef.current = "";
            setAddOpen(true);
          }}
          className="flex h-[50px] items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#0f1720]"
        >
          + Room
        </button>
      ) : null}

      {primary?.kind === "extras" && !onboardComplete ? (
        <p className="px-1 text-[12.5px] font-medium leading-snug text-[#8494a2]">
          {primary.room.name} is captured but {primary.room.extrasLeft} thing
          {primary.room.extrasLeft === 1 ? "" : "s"} still need a guest place. Finish it before you walk out.
        </p>
      ) : null}

      {rooms.length === 0 && !addOpen ? (
        <button
          type="button"
          onClick={() => {
            typedRoomRef.current = "";
            setAddOpen(true);
          }}
          className="h-14 w-full touch-manipulation rounded-[14px] bg-[#24a89a] text-base font-bold text-white"
        >
          + Room
        </button>
      ) : onboardComplete ? (
        <button
          type="button"
          onClick={() => startCapture()}
          className="h-14 w-full rounded-[14px] bg-[#24a89a] text-base font-bold text-white"
        >
          Capture a room
        </button>
      ) : primary ? (
        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() =>
              primary.kind === "extras" ? startExtras(primary.room.name) : startCapture(primary.room.name)
            }
            className="h-14 w-full rounded-[14px] bg-[#24a89a] text-base font-bold text-white"
          >
            {primaryButtonLabel(primary)}
          </button>
          {secondaryCapture ? (
            <button
              type="button"
              onClick={() => startCapture(secondaryCapture.name)}
              className="flex h-[50px] items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[15px] font-bold text-[#0f1720]"
            >
              Capture {secondaryCapture.name}
            </button>
          ) : null}
        </div>
      ) : null}

      {pendingRename ? (
        <Sheet title="Rename room" onClose={() => !busy && setPendingRename(null)}>
          <input
            value={renameValue}
            autoFocus
            autoComplete="off"
            autoCapitalize="words"
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void renameRoom();
              }
            }}
            className="h-12 w-full rounded-[12px] border-[1.5px] border-[#e6edf1] px-3 text-[16px] text-[#0f1720] outline-none"
          />
          <button
            type="button"
            disabled={busy || !renameValue.trim()}
            onClick={() => void renameRoom()}
            className="mt-4 h-12 w-full rounded-[14px] bg-[#24a89a] text-[15px] font-bold text-white disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </Sheet>
      ) : null}

      {pendingRemove ? (
        <Sheet title="Remove room" onClose={() => setPendingRemove(null)}>
          <p className="text-[15px] font-medium text-[#64748b]">
            Remove {pendingRemove.name}? You can add it again later.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void removeRoom(pendingRemove.id, pendingRemove.name)}
            className="mt-4 h-12 w-full rounded-[14px] bg-[#e24b4b] text-[15px] font-bold text-white disabled:opacity-50"
          >
            {busy ? "Removing…" : "Remove"}
          </button>
        </Sheet>
      ) : null}
    </div>
  );
}

function HandoffScreen({
  handoff,
  rows,
  doneCount,
  onStartExtras,
  onSkipToNext,
  onGoNext,
  onClose,
}: {
  handoff: Handoff;
  rows: RoomSpineRow[];
  doneCount: number;
  onStartExtras: () => void;
  onSkipToNext: () => void;
  onGoNext: () => void;
  onClose: () => void;
}) {
  if (typeof document === "undefined") return null;

  if (handoff.kind === "imported") {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex flex-col bg-white" data-ptr-ignore>
        <div className="flex justify-end px-5 pt-6">
          <div className="text-[13px] font-bold tracking-wide text-[#8494a2]">{handoff.room}</div>
        </div>
        <div className="flex flex-1 flex-col justify-center gap-4 px-7">
          <div className="flex h-[52px] w-[52px] items-center justify-center rounded-full border-[1.5px] border-[#cfe6e2] bg-[#e4f2f0] text-[22px] font-bold text-[#15776d]">
            ✓
          </div>
          <div className="text-[30px] font-bold leading-tight tracking-[-0.7px] text-[#0f1720]">
            {handoff.room} imported.
          </div>
          <p className="text-[17px] font-medium leading-snug text-[#475569]">
            {handoff.extras} thing{handoff.extras === 1 ? "" : "s"} need a guest place while you’re still here.
          </p>
          <p className="text-[13.5px] font-medium leading-snug text-[#8494a2]">
            Where it lives and a photo of that place. Two taps each, then {handoff.room} is done for good.
          </p>
        </div>
        <div className="flex flex-col items-center gap-3.5 px-5 pb-8 pt-3">
          <button
            type="button"
            onClick={onStartExtras}
            className="flex h-14 w-full items-center justify-center rounded-[14px] bg-[#24a89a] text-[17px] font-bold text-white"
          >
            Start extras
          </button>
          {handoff.nextName ? (
            <button type="button" onClick={onSkipToNext} className="text-[12.5px] font-medium text-[#a3b0b6]">
              Do {handoff.nextName} first
            </button>
          ) : (
            <button type="button" onClick={onClose} className="text-[12.5px] font-medium text-[#a3b0b6]">
              Back to rooms
            </button>
          )}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-white" data-ptr-ignore>
      <div className="flex justify-end px-5 pt-6">
        <div className="text-[13px] font-bold tracking-wide text-[#8494a2]">
          {doneCount} of {rows.length} rooms done
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-center gap-5 px-7">
        <div>
          <div className="text-[30px] font-bold leading-tight tracking-[-0.7px] text-[#0f1720]">
            {handoff.room} extras done.
          </div>
          <p className="mt-3 text-[16px] font-medium leading-snug text-[#475569]">
            {handoff.nextName ? `Nothing left in this room. Walk to the ${handoff.nextName.toLowerCase()}.` : "Nothing left in this room."}
          </p>
        </div>
        <div className="overflow-hidden rounded-[18px] border border-[#e6edf1] bg-[#f4f7f9]">
          {rows.map((row, i) => (
            <div
              key={row.id}
              className={`flex items-center gap-2.5 px-4 py-3 ${i > 0 ? "border-t border-[#e9eff2]" : ""}`}
            >
              <div
                className={`h-5 w-5 shrink-0 rounded-full border-[1.5px] ${
                  row.state === "extras_done" ? "border-[#24a89a] bg-[#24a89a]" : "border-[#d7e0e6] bg-white"
                }`}
              />
              <div className="min-w-0 flex-1 text-[14.5px] font-bold text-[#0f1720]">{row.name}</div>
              <div className="text-[12.5px] font-medium text-[#8494a2]">{row.meta}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-5 pb-8 pt-3">
        {handoff.nextName ? (
          <button
            type="button"
            onClick={onGoNext}
            className="flex h-14 w-full items-center justify-center rounded-[14px] bg-[#24a89a] text-[17px] font-bold text-white"
          >
            Go to {handoff.nextName}
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="flex h-14 w-full items-center justify-center rounded-[14px] bg-[#24a89a] text-[17px] font-bold text-white"
          >
            Onboarding complete
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

function AddRoomSheet({
  busy,
  inputRef,
  onClose,
  onTyped,
  onSave,
}: {
  busy: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  onClose: () => void;
  onTyped: (value: string) => void;
  onSave: () => void;
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-white"
      data-ptr-ignore
      role="dialog"
      aria-modal="true"
    >
      <div
        className="mx-auto flex w-full max-w-lg flex-col px-5"
        style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="text-[18px] font-bold text-[#0f1720]">New room</div>
          <button type="button" className="text-[15px] font-medium text-[#64748b]" onClick={onClose}>
            Cancel
          </button>
        </div>
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          placeholder="e.g. Kitchen"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          name="stravo-room-title"
          enterKeyHint="go"
          className="h-12 w-full rounded-[12px] border-[1.5px] border-[#e6edf1] px-3 text-[#0f1720]"
          style={{ fontSize: 16, WebkitUserSelect: "text", userSelect: "text" }}
          onInput={(e) => onTyped(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSave();
            }
          }}
        />
        <button
          type="button"
          disabled={busy}
          className="mt-3 h-12 w-full touch-manipulation rounded-[14px] bg-[#24a89a] text-[16px] font-bold text-white disabled:opacity-40"
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSave();
          }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      data-ptr-ignore
      role="dialog"
      aria-modal="true"
    >
      <div className="relative z-10 w-full max-w-md rounded-[22px] bg-white px-5 pb-6 pt-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-[20px] font-bold text-[#0f1720]">{title}</div>
          <button type="button" onClick={onClose} className="text-[15px] font-medium text-[#64748b]">
            Cancel
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
